'use client';

import { useState, useEffect } from 'react';
import {
  Query,
  onSnapshot,
  DocumentData,
  FirestoreError,
  QuerySnapshot,
  CollectionReference,
} from 'firebase/firestore';
import { errorEmitter } from '@/firebase/error-emitter';
import { FirestorePermissionError } from '@/firebase/errors';
import { confirmedSnapshots } from '@/lib/confirmed-snapshot';

/** Utility type to add an 'id' field to a given type T. */
export type WithId<T> = T & { id: string };

/**
 * Interface for the return value of the useCollection hook.
 * @template T Type of the document data.
 */
export interface UseCollectionResult<T> {
  data: WithId<T>[] | null; // Document data with ID, or null.
  isLoading: boolean;       // True if loading.
  error: FirestoreError | Error | null; // Error object, or null.
  /**
   * With `serverAnswer`: the server has not answered within
   * SERVER_ANSWER_TIMEOUT_MS (Firestore blocked or too slow on this network).
   * `data` stays null meanwhile. Always false without the option.
   */
  unreachable: boolean;
}

/**
 * How long a `serverAnswer` listener waits before calling the server
 * unreachable: a little past the SDK's own ten seconds before it falls back to
 * this browser's cache, so a slow answer still lands first.
 */
export const SERVER_ANSWER_TIMEOUT_MS = 12_000;

/* Internal implementation of Query:
  https://github.com/firebase/firebase-js-sdk/blob/c5f08a9bc5da0d2b0207802c972d53724ccef055/packages/firestore/src/lite-api/reference.ts#L143
*/
export interface InternalQuery extends Query<DocumentData> {
  _query: {
    path: {
      canonicalString(): string;
      toString(): string;
    }
  }
}

/**
 * React hook to subscribe to a Firestore collection or query in real-time.
 * Handles nullable references/queries.
 * 
 *
 * IMPORTANT! YOU MUST MEMOIZE the inputted memoizedTargetRefOrQuery or BAD THINGS WILL HAPPEN
 * use useMemo to memoize it per React guidence.  Also make sure that it's dependencies are stable
 * references
 *  
 * SERVER ANSWER. With `{ serverAnswer: true }` the result is only ever an
 * answer the SERVER gave (src/lib/confirmed-snapshot.ts). Without it, the
 * first result can be this browser's cache: on a network where Firestore is
 * blocked the SDK falls back to it after about ten seconds, usually holding
 * nothing, and a listing read that as "no products" (2026-10-26 audit, item
 * 13). Use it where an empty result is shown as a fact about the catalogue.
 *
 * @template T Optional type for document data. Defaults to any.
 * @param {CollectionReference<DocumentData> | Query<DocumentData> | null | undefined} targetRefOrQuery -
 * The Firestore CollectionReference or Query. Waits if null/undefined.
 * @param options `serverAnswer`: see above.
 * @returns {UseCollectionResult<T>} Object with data, isLoading, error, unreachable.
 */
export function useCollection<T = any>(
    memoizedTargetRefOrQuery: ((CollectionReference<DocumentData> | Query<DocumentData>) & {__memo?: boolean})  | null | undefined,
    options?: { serverAnswer?: boolean },
): UseCollectionResult<T> {
  type ResultItemType = WithId<T>;
  type StateDataType = ResultItemType[] | null;

  const [data, setData] = useState<StateDataType>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<FirestoreError | Error | null>(null);
  const [unreachable, setUnreachable] = useState(false);
  const serverAnswer = options?.serverAnswer === true;

  // Throw BEFORE the effect runs so an unmemoized ref can't queue a live
  // onSnapshot subscription that then leaks when the render unwinds. In
  // StrictMode this matters: the cleanup tied to the effect won't fire if
  // the render itself throws, leaving the listener active.
  if (memoizedTargetRefOrQuery && !memoizedTargetRefOrQuery.__memo) {
    throw new Error(memoizedTargetRefOrQuery + ' was not properly memoized using useMemoFirebase');
  }

  useEffect(() => {
    if (!memoizedTargetRefOrQuery) {
      setData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setUnreachable(false);

    const unreachableTimer = serverAnswer
      ? setTimeout(() => setUnreachable(true), SERVER_ANSWER_TIMEOUT_MS)
      : undefined;
    const onNext = (snapshot: QuerySnapshot<DocumentData>) => {
      clearTimeout(unreachableTimer);
      const results: ResultItemType[] = [];
      for (const doc of snapshot.docs) {
        results.push({ ...(doc.data() as T), id: doc.id });
      }
      setData(results);
      setError(null);
      setUnreachable(false);
      setIsLoading(false);
    };

    // Directly use memoizedTargetRefOrQuery as it's assumed to be the final query
    const unsubscribe = onSnapshot(
      memoizedTargetRefOrQuery,
      { includeMetadataChanges: serverAnswer },
      serverAnswer
        ? confirmedSnapshots<QuerySnapshot<DocumentData>>((snapshot, confirmed) => { if (confirmed) onNext(snapshot); })
        : onNext,
      (error: FirestoreError) => {
        clearTimeout(unreachableTimer);
        // This logic extracts the path from either a ref or a query
        const path: string =
          memoizedTargetRefOrQuery.type === 'collection'
            ? (memoizedTargetRefOrQuery as CollectionReference).path
            : (memoizedTargetRefOrQuery as unknown as InternalQuery)._query.path.canonicalString()

        // Only a rules denial is a permission error. Anything else (an index
        // still building, a quota, the listener being cut off) is returned as
        // itself, not dressed up as a denial, and is not broadcast.
        if (error.code !== 'permission-denied') {
          console.error(`Firestore listener on ${path} failed (${error.code}):`, error);
          setError(error)
          setData(null)
          setIsLoading(false)
          return;
        }

        const contextualError = new FirestorePermissionError({
          operation: 'list',
          path,
        })

        setError(contextualError)
        setData(null)
        setIsLoading(false)

        // Reported to FirebaseErrorListener, which logs it in production and
        // throws it in development. The caller already has it via `error`.
        errorEmitter.emit('permission-error', contextualError);
      }
    );

    return () => { clearTimeout(unreachableTimer); unsubscribe(); };
  }, [memoizedTargetRefOrQuery, serverAnswer]); // Re-run if the target query/reference changes.
  return { data, isLoading, error, unreachable };
}